import React, { useContext, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import {
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Divider from '@components/Divider'
import Form from '@components/Form'
import Loader from '@components/Loader'
import { useSortAndLabelStudentList } from '@hooks/useSortAndLabelStudentList'
import { useStudentListSortedByFilterString } from '@hooks/useStudentListSortedByFilterString'
import PropTypes from 'prop-types'
import { useSchool } from '@hooks/useSchool'
import { useCurrentUser } from '@hooks/useCurrentUser'

const AddStudentToSchoolResource = (props) => {
  const {
    resource,
    schoolId,
    resourceError,
    handleSubmit: onSubmit,
    isPosting,
    panelType,
  } = props
  const [, dispatch] = useContext(AppContext)
  const { currentUser } = useCurrentUser()
  const [students, setStudents] = useState(props.students)
  const { school } = useSchool(schoolId)
  const { studentOptions } = useSortAndLabelStudentList(students)

  const [filterString, setFilterString] = useState('')
  const sortedStudentOptions = useStudentListSortedByFilterString(filterString, studentOptions)

  useEffect(() => {
    if (resource && school) {
      const studentIdAlreadyInResource = resource?.students.map(
        (student) => student.id
      )
      let filteredStudents = school.students.filter(
        (student) => !studentIdAlreadyInResource.includes(student.id) // filter out students who are already in resource
      )

      if (!currentUser?.hasPaidLicense) {
        const myClass = currentUser.schoolClasses.find((sc) => sc.isPrivate)
        filteredStudents = filteredStudents.filter(
          (student) =>
            student.schoolClasses.some((sc) => sc.id === myClass.id) &&
            student.email === null &&
            student.username
        )
      } else {
        const currentUserClassIds = currentUser.schoolClasses
          .filter((sc) => !sc.isPrivate)
          .map((sc) => sc.id)

        filteredStudents = filteredStudents.filter(
          (student) =>
            student.email !== null &&
            !student.username && // student is not anonymous
            student.schoolClasses.some(
              (sc) => currentUserClassIds.includes(sc.id) // is in users classes
            )
        )
      }

      setStudents(filteredStudents)
    }
  }, [resource, school])

  const { handleSubmit, errors, control, formState } = useForm({
    mode: 'onChange',
  })
  const { isValid } = formState

  return (
    <>
      <div className={'relative'}>
        <div className='absolute top-0 right-0'>
          <Button
            handleClick={() => {
              dispatch(closePanel(panelType))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          ></Button>
        </div>
      </div>

      <div className={'flex flex-col items-center'}>
        <Text>{resource?.name ?? ''}</Text>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
          Lägg till elever
        </Heading>
      </div>

      <Divider size={DIVIDER_SIZE.SMALL} />

      {!resource && !resourceError && <Loader />}
      {resourceError && <Text>Error Loading Students</Text>}

      {resource && !resourceError && sortedStudentOptions && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Label htmlFor='students'>Välj elever*</Form.Label>
          <Controller
            control={control}
            name={'students'}
            defaultValue={[]}
            rules={{
              validate: (value) => value.length > 0,
            }}
            render={(
              { onChange, onBlur, value, name, ref },
              { invalid, isTouched, isDirty }
            ) => (
              <Form.MultiSelect
                name={name}
                onChange={(options) => onChange(options)}
                onInputChange={(newValue, actionMeta) => {
                  if (actionMeta.action === 'input-change') {
                    setFilterString(newValue)
                  }
                }}
                options={sortedStudentOptions}
                placeholder={'Sök elever'}
                isMulti
                isSearchable
              />
            )}
          />
          {errors.students && (
            <Form.ErrorMessage>En eller flera elever behövs</Form.ErrorMessage>
          )}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <ButtonWithLoader
            type={BUTTON_TYPE.PRIMARY}
            isLoading={isPosting}
            disabledText={'Lägg till'}
            enabledText={'Lägg till'}
            disabled={!isValid || isPosting}
          />
        </Form>
      )}
    </>
  )
}

AddStudentToSchoolResource.propTypes = {
  handleSubmit: PropTypes.func,
  resource: PropTypes.object,
  school: PropTypes.object,
  resourceError: PropTypes.object,
  panelType: PropTypes.string,
}

export default AddStudentToSchoolResource
